import React, { useCallback, useState } from "react";

import { Form, ModalDialog, SelectField, SubmitButton } from "@culturehq/components";
import { SelectOption, SelectValueWithCategory } from "@culturehq/components/dist/components/form/typings";

import { ApprovedForSocial, LandingPage, Story } from "chq";
import { useDoor, useTryCatch } from "app/hooks";
import { t, Trans } from "app/utils";

import ChqModalDialog from "components/common/ChqModalDialog";
import { FormValues } from "@culturehq/components/dist/components/form/Form";
import { makePatch } from "@culturehq/client";
import ErrorMessage from "components/common/ErrorMessage";

interface CulturePageSelectorModalProps {
  approvedContent?: ApprovedForSocial;
  children: any;
  landingPages: LandingPage[];
  onUpdateApprovedForSocial: (value: ApprovedForSocial | Story | Record<string, string[]>) => void;
}

const CulturePageSelectorModal: React.FC<CulturePageSelectorModalProps> = ({
  approvedContent, children, landingPages, onUpdateApprovedForSocial
}) => {
  const [changed, setChanged] = useState(false);
  const { open, onOpen, onClose: onCloseModal } = useDoor(false);

  const restartState = useCallback(() => {
    setChanged(false);
    onCloseModal();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onConfirmClose = useCallback(
    () => {
      const message = t("You have unsaved changes, are you sure you'd like to close the culture page modal?");

      // eslint-disable-next-line no-alert
      if (!changed || window.confirm(message)) restartState();
    },
    [changed, restartState]
  );

  const [{ error }, onSubmit] = useTryCatch(
    async ({ culturePages }: FormValues) => {
      const { landingPagesIds }: { landingPagesIds: string[] } = await makePatch(`/stories/landing_page/${approvedContent?.entity?.id}`, {
        landingPageIds: culturePages as string[]
      });
      onUpdateApprovedForSocial({
        ...approvedContent,
        landingPagesIds: landingPagesIds.map(Number),
        entity: {
          ...approvedContent?.entity,
          externalState: landingPagesIds.length > 0 ? "landing_page" : "approved"
        } as Story
      } as ApprovedForSocial);
      restartState();
    },
    []
  );

  return (
    <>
      {children(onOpen)}
      {open && (
        <ChqModalDialog onClose={onConfirmClose} className="culture-page-selector-modal">
          <ModalDialog.Heading onClose={onConfirmClose}>
            <Trans>Culture Page</Trans>
          </ModalDialog.Heading>
          <ModalDialog.Body>
            {error && <ErrorMessage error={error} />}
            <div className="culture-page-selector-modal_info">
              <Trans>Use the field below to assign this story to one or more culture pages.</Trans>
            </div>
            <Form
              initialValues={{
                culturePages: approvedContent?.landingPagesIds?.map(id => id.toString()) || []
              }}
              onSubmit={onSubmit}
            >
              <SelectField
                name="culturePages"
                options={landingPages.map(landingPage => ({
                  label: landingPage.name,
                  value: `${landingPage.id}`
                } as SelectOption))}
                placeholder="Please select a Culture Page"
                multiple
              >
                <Trans>Culture Pages</Trans>
              </SelectField>
              <SubmitButton primary>
                {submitting => (
                  submitting
                    ? <Trans>Saving...</Trans> : <Trans>Save</Trans>
                )}
              </SubmitButton>
            </Form>
          </ModalDialog.Body>
        </ChqModalDialog>
      )}
    </>
  );
};

export default CulturePageSelectorModal;
